import React, { useEffect, useState } from 'react';
import {
  Col,
  Flex,
  Input,
  Layout,
  Modal,
  Row,
  Select,
  Typography,
  message,
} from 'antd';
import queryString from 'query-string';
import omit from 'lodash/omit';
import startCase from 'lodash/startCase';
import { TableList } from 'components/table-list';
import PdButton from 'components/pd-button';
import { errHandling, ACTIVE_INACTIVE_STATUS_FILTER_LIST } from 'utils';
import PickUpPointService from 'services/pickup-point';
import { useLocation, useNavigate } from 'react-router-dom';

const { Content } = Layout;
const { Option } = Select;
const { Search } = Input;
const { Text } = Typography;
const { confirm } = Modal;

export function PickUpPoint() {
  const location = useLocation();
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(1);
  const [loadStatus, setLoadStatus] = useState(false);
  const [nextPage, setNextPage] = useState(1);
  const [pickupPointList, setPickUpPointList] = useState([]);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');

  const parsed = queryString.parse(location.search);

  const columns = [
    {
      name: 'Pick Up Point',
      selector: row => row.title,
      width: "40%",
     cell: (row) => (
      <div
        style={{
          width: "100%",
          minWidth: 0,
        }}
      >
        <div
          style={{
            display: "-webkit-box",
            WebkitBoxOrient: "vertical",
            WebkitLineClamp: 2,
            overflow: "hidden",
            textOverflow: "ellipsis",
            wordBreak: "break-word",
          }}
        >
          {row.title}
        </div>
      </div>
    ),
    },
    {
      name: 'ID Pick Up Point',
      selector: row => row.pickup_id,
      center: true,
      width: "15%"
    },
    {
      name: 'Warehouse',
      selector: row => row.warehouse.title,
      center: true,
    },
    {
      name: 'Status',
      selector: row => row.status,
      center: true,
    },
    {
      width: '25%',
      center: true,
      cell: ActionButton,
    },
  ];

  function ActionButton({ status, id }) {
    const buttonProps = {
      active: {
        primary: {
          label: 'Edit',
          action() {
            navigate(`/pickup_points/location/create-edit?id=${id}`);
          },
        },
        secondary: {
          label: 'Deactivate',
          action: deactivatePickupPoint.bind(this, id),
        },
        type: 'primary',
      },
      inactive: {
        primary: {
          label: 'Delete',
          action() {
            confirm({
              title: 'Are you sure delete this pickup point?',
              okText: 'Yes',
              okType: 'danger',
              cancelText: 'Cancel',
              onOk() {
                deletePickUpPoint(id);
              },
            });
          },
        },
        secondary: {
          label: 'Reactivate',
          action: reactivatePickUpPoint.bind(this, id),
        },
        type: 'danger',
      },
    };

    return (
      <Flex gap={8} className="w-full" style={{justifyContent : "end"}}>
          <PdButton
            className="height-32"

            type={buttonProps[status].type}
            onClick={buttonProps[status].secondary.action}
            outline>
            {buttonProps[status].secondary.label}
          </PdButton>

          <PdButton
            className="height-32"

            onClick={buttonProps[status].primary.action}
            type={buttonProps[status].type}>
            {buttonProps[status].primary.label}
          </PdButton>
      </Flex>
    );
  }

  function resetFilter() {
    setSearch('');
    setStatus('');
    setCurrentPage(1);
    const stringified = queryString.stringify(
      omit(parsed, ['q', 'status', 'page'])
    );
    navigate({
      pathname: '/pickup_points/location',
      search: stringified,
    });
  }

  function onChangeStatus(value) {
    setStatus(value);
    parsed.status = value;
    parsed.page = 1;
    const stringified = queryString.stringify(parsed);
    navigate({
      pathname: '/pickup_points/location',
      search: stringified,
    });
  }

  function changePage(page) {
    if (page < 1) page = 1;
    parsed.page = page;
    const stringified = queryString.stringify(parsed);
    navigate({
      pathname: '/pickup_points/location',
      search: stringified,
    });
  }

  function handleOnSearch(value) {
    if (!value) return;
    parsed.q = value;
    parsed.page = 1;
    const stringified = queryString.stringify(parsed);
    navigate({
      pathname: '/pickup_points/location',
      search: stringified,
    });
  }

  async function deactivatePickupPoint(id) {
    setLoadStatus(true);
    try {
      await PickUpPointService.deactivatePickUpPoint(id);
      await fetchPickUpPointList();
      message.success('deactivated');
    } catch (error) {
      errHandling(error);
    } finally {
      setLoadStatus(false);
    }
  }

  async function reactivatePickUpPoint(id) {
    setLoadStatus(true);
    try {
      await PickUpPointService.reactivatePickUpPoint(id);
      await fetchPickUpPointList();
      message.success('reactivated');
    } catch (error) {
      errHandling(error);
    } finally {
      setLoadStatus(false);
    }
  }

  async function deletePickUpPoint(id) {
    setLoadStatus(true);
    try {
      await PickUpPointService.deletePickUpPoint(id);
      await fetchPickUpPointList();
      message.success('deleted');
    } catch (error) {
      errHandling(error);
    } finally {
      setLoadStatus(false);
    }
  }

  async function fetchPickUpPointList({
    limit = 10,
    cursor = parsed.page || currentPage,
    q = parsed.q,
    status = parsed.status,
    warehouse_id = parsed.warehouse_id,
  } = {}) {
    setLoadStatus(true);
    try {
      const { data: { data, metadata: { next_cursor } = {} } = {} } =
        await PickUpPointService.getPickUpPointList({
          limit,
          cursor,
          q,
          status,
          warehouse_id,
        });

      setPickUpPointList(data);
      setNextPage(next_cursor);
      if (parsed.page) setCurrentPage(parsed.page);
    } catch (error) {
      errHandling(error);
    } finally {
      setLoadStatus(false);
    }
  }

  function handleOnChangeSearch({ target: { value } } = {}) {
    setSearch(value);
  }

  useEffect(() => {
    fetchPickUpPointList();
    // eslint-disable-next-line
  }, [parsed.status, parsed.q, parsed.page]);

  return (
    <Layout className="main-container">
      <div className="content">
        <div className="pageTitle">Pick Up Point</div>
        <Content className="pageContent">
          <Row
            gutter={[18, 36]}
            className="pd-margin-bottom-md"
            type="flex"
            align="middle">
            <Col lg={7}>
              <Search
                value={search}
                onChange={handleOnChangeSearch}
                enterButton
                className="search-product"
                placeholder="Search by pickup point/id"
                onSearch={handleOnSearch}
              />
            </Col>
            <Col lg={3}>
              <Select
                className="w-full"
                onChange={onChangeStatus}
                value={status}
                placeholder="Status">
                {ACTIVE_INACTIVE_STATUS_FILTER_LIST.map((statusEl, idx) => (
                  <Option
                    hidden={!statusEl}
                    key={idx}
                    selected={!statusEl}
                    value={statusEl}>
                    {startCase(statusEl) || 'Status'}
                  </Option>
                ))}
              </Select>
            </Col>
            <Col lg={4}>
              <Text className="reset-filter" onClick={resetFilter}>
                Reset Filter
              </Text>
            </Col>
            <Col lg={{ span: 5, offset: 5 }}>
              <PdButton
                type="primary"
                full
                onClick={() => navigate('/pickup_points/location/create-edit')}>
                Create Pick Up Point
              </PdButton>
            </Col>
          </Row>
          <Row>
            <TableList
              changePage={changePage}
              columns={columns}
              loadingOrder={loadStatus}
              nextPage={nextPage}
              orderList={pickupPointList}
              page={currentPage}
              responsive={false}
            />
          </Row>
        </Content>
      </div>
    </Layout>
  );
}
